import React from 'react';
import { Button, Dropdown, Input, Select, Space } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownOutlined } from '@ant-design/icons';
import type { EditingData, UserTableRecord } from './useUsersData';

const { Option } = Select;

export interface GetUserColumnsOptions {
  editingUserId: string | null;
  editingData: EditingData;
  setEditingData: React.Dispatch<React.SetStateAction<EditingData>>;
  isSuperUser: boolean;
  isAuthApiAdmin: boolean;
  isAuthApiOwner: boolean;
  currentUserId: string | null;
  isUpdating: boolean;
  deletingUserId: string | null;
  timezone: string;
  onEditUser: (userId: string, name: string, role: string) => void;
  onSaveEdit: (userId: string) => void;
  onCancelEdit: () => void;
  onRequestDelete: (target: { userId: string; userName: string }) => void;
  onRequestTransferOwnership: (target: { userId: string; userName: string }) => void;
}

export const getUserColumns = ({
  editingUserId,
  editingData,
  setEditingData,
  isSuperUser,
  isAuthApiAdmin,
  isAuthApiOwner,
  currentUserId,
  isUpdating,
  deletingUserId,
  timezone,
  onEditUser,
  onSaveEdit,
  onCancelEdit,
  onRequestDelete,
  onRequestTransferOwnership,
}: GetUserColumnsOptions): ColumnsType<UserTableRecord> => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: '18%',
    sorter: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
    render: (text: string, record) => {
      if (editingUserId === record.key) {
        // Name is read-only in edit mode — the updateUser mutation no longer
        // accepts a name field, so the row shows the current name without
        // letting the admin change it.
        return <Input value={editingData.name} disabled />;
      }
      return text;
    },
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: '25%',
    sorter: (a, b) => (a?.email || '').localeCompare(b?.email || ''),
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    width: '12%',
    sorter: (a, b) => (a?.role || '').localeCompare(b?.role || ''),
    render: (text: string, record) => {
      if (editingUserId === record.key) {
        return (
          <Select
            value={editingData.role}
            onChange={(value) => setEditingData({ ...editingData, role: value })}
            placeholder="Select a role"
            style={{ width: '100%' }}
            className="[&_.ant-select-selector]:!bg-surface"
          >
            <Option value="Member">Member</Option>
            <Option value="Admin">Admin</Option>
            {isSuperUser && <Option value="Owner">Owner</Option>}
          </Select>
        );
      }
      return text;
    },
  },
  {
    title: 'Last Active',
    dataIndex: 'lastActiveAt',
    key: 'lastActiveAt',
    width: '22%',
    sorter: (a, b) => {
      if (!a.lastActiveAt && !b.lastActiveAt) return 0;
      if (!a.lastActiveAt) return 1;
      if (!b.lastActiveAt) return -1;
      return new Date(a.lastActiveAt).getTime() - new Date(b.lastActiveAt).getTime();
    },
    render: (text: string | undefined) => {
      if (!text) return '-';
      const date = new Date(text);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
  },
  {
    title: 'Action',
    key: 'action',
    width: '15%',
    render: (_: unknown, record) => {
      // Role hierarchy gating:
      // - Cannot edit/delete yourself (backend also prevents self-role-change)
      // - Superuser can edit/delete anyone else
      // - Owner can edit/delete Admins and Members, but not other Owners
      // - Admin can edit/delete Admins and Members, but not Owners
      const isSelf = record.id === currentUserId;
      const isHigherRole = record.role === 'Owner' || record.role === 'Superuser';
      const canEdit =
        !isSelf &&
        (isSuperUser ||
          (isAuthApiOwner && !isHigherRole) ||
          (isAuthApiAdmin && !isAuthApiOwner && !isHigherRole));
      const canDelete = canEdit;
      const canTransferOwnership = !isSelf && isAuthApiOwner && !isHigherRole;

      if (editingUserId === record.key) {
        return (
          <Space size="middle">
            <Button type="link" onClick={() => onSaveEdit(record.key)} loading={isUpdating}>
              Save
            </Button>
            <Button type="link" onClick={onCancelEdit}>
              Cancel
            </Button>
          </Space>
        );
      }

      const menuItems: MenuProps['items'] = [];
      if (canDelete) {
        menuItems.push({
          key: 'delete',
          label: 'Delete User',
          onClick: () => onRequestDelete({ userId: record.id, userName: record.name }),
        });
      }
      if (canTransferOwnership) {
        menuItems.push({
          key: 'transfer',
          label: 'Transfer Ownership',
          onClick: () => onRequestTransferOwnership({ userId: record.id, userName: record.name }),
        });
      }

      const hasMoreActions = menuItems.length > 0;

      return (
        <Space size="middle">
          {canEdit && (
            <Button type="link" onClick={() => onEditUser(record.key, record.name, record.role)}>
              Edit
            </Button>
          )}
          {hasMoreActions && (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              disabled={deletingUserId === record.key}
            >
              <Button
                type="link"
                loading={deletingUserId === record.key}
                onClick={(e) => e.preventDefault()}
              >
                More <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </Space>
      );
    },
  },
];
